import React, { useEffect, useState } from "react";
import Chip from "@material-ui/core/Chip";
import { withStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import queryString from "query-string";
import { RouteComponentProps } from "react-router";
import { BATCH_STATUS } from "./FilteringBatchesDialog";
import t from "../../../locales/t";
import BatchType from "../../../types/Batch";
import constants from "../../../tools/constants";
import { tableFilterChipsStyles } from "../../common/tableTheme";

interface Props {
  classes: Record<string, string>;
  urlParams: queryString.ParsedQuery<string>;
  batches: BatchType[];
  searchTerm: string;
  userId?: number | string;
  history: RouteComponentProps["history"];
  appFetch: Function;
  withReset?: boolean;
}

const FILTER_QUERY_KEYS = [
  "projectId",
  "batchState",
  "searchTerm",
  "batchSearchMode",
  "labels",
  "userId",
  "batchIds",
];

const projectNameById: Record<string, string> = {};

const resolveProjectNameFromBatches = (
  batches: BatchType[],
  projectId: string
) =>
  batches.find((batch) => String(batch.project?.id) === projectId)?.project
    ?.name;

const useProjectName = (
  projectId: string | undefined,
  batches: BatchType[],
  appFetch: Props["appFetch"]
) => {
  const [name, setName] = useState<string | undefined>(() =>
    projectId ? projectNameById[projectId] : undefined
  );

  useEffect(() => {
    if (!projectId) {
      setName(undefined);
      return;
    }

    const cached = projectNameById[projectId];
    if (cached) {
      setName(cached);
      return;
    }

    const fromBatches = resolveProjectNameFromBatches(batches, projectId);
    if (fromBatches) {
      projectNameById[projectId] = fromBatches;
      setName(fromBatches);
      return;
    }

    let cancelled = false;
    appFetch(`${constants.paths.PROJECTS_PATH}/${projectId}`)
      .then((project: { name?: string }) => {
        if (cancelled || !project?.name) return;
        projectNameById[projectId] = project.name;
        setName(project.name);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [projectId, batches, appFetch]);

  return name;
};

const getBatchStateLabel = (batchState: string) => {
  const entry = Object.values(BATCH_STATUS).find((s) => s.filter === batchState);
  return entry ? t(entry.label) : batchState;
};

const BatchFilterChips = ({
  classes,
  urlParams,
  batches,
  searchTerm,
  userId,
  history,
  appFetch,
  withReset = true,
}: Props) => {
  const pushQuery = (next: Record<string, unknown>) => {
    history.push(
      `/batches/0?${queryString.stringify(next, { arrayFormat: "none" })}`
    );
  };

  const removeKeys = (...keys: string[]) => {
    const next = { ...urlParams };
    keys.forEach((key) => {
      delete next[key];
    });
    pushQuery(next);
  };

  const handleResetAll = () => {
    const next = { ...urlParams };
    FILTER_QUERY_KEYS.forEach((key) => {
      delete next[key];
    });
    pushQuery(next);
  };

  const projectId =
    typeof urlParams.projectId === "string" ? urlParams.projectId : undefined;
  const projectName = useProjectName(projectId, batches, appFetch);
  const batchState =
    typeof urlParams.batchState === "string" ? urlParams.batchState : undefined;
  const hasUserScope = typeof urlParams.userId !== "undefined";
  const hasHistoryScope = typeof urlParams.batchIds !== "undefined";

  const chips: { key: string; label: string; onDelete: () => void }[] = [];

  if (projectId) {
    chips.push({
      key: "projectId",
      label: projectName ? `Projet · ${projectName}` : "Projet · …",
      onDelete: () => removeKeys("projectId"),
    });
  }

  if (batchState) {
    chips.push({
      key: "batchState",
      label: `Statut · ${getBatchStateLabel(batchState)}`,
      onDelete: () => removeKeys("batchState"),
    });
  }

  if (searchTerm) {
    chips.push({
      key: "searchTerm",
      label: `Recherche · ${searchTerm}`,
      onDelete: () => removeKeys("searchTerm"),
    });
  }

  if (hasUserScope && String(urlParams.userId) === String(userId)) {
    chips.push({
      key: "userId",
      label: "Mes lots",
      onDelete: () => removeKeys("userId"),
    });
  }

  if (hasHistoryScope) {
    chips.push({
      key: "batchIds",
      label: "Derniers lots consultés",
      onDelete: () => removeKeys("batchIds"),
    });
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className={classes.filterChipsRow}>
      <span className={classes.filterChipsLabel}>Filtres :</span>
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          label={chip.label}
          onDelete={chip.onDelete}
          deleteIcon={<CloseIcon />}          
          className={classes.filterChip}
          classes={{ deleteIcon: classes.filterChipDeleteIcon }}
        />
      ))}
      {withReset && chips.length > 1 && (
        <button
          type="button"
          className={classes.filterChipsResetLink}
          onClick={handleResetAll}
        >
          Réinitialiser
        </button>
      )}
    </div>
  );
};

export default withStyles(tableFilterChipsStyles)(BatchFilterChips);
